import React, { useState, useEffect } from 'react';
import { sustainabilityAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Leaf, Zap, FileText, Globe } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSustainability() {
  const [metrics, setMetrics] = useState(null);
  const [report, setReport] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [m, r] = await Promise.all([sustainabilityAPI.getMetrics(), sustainabilityAPI.getReport()]);
      setMetrics(m.data);
      setReport(r.data);
      setForm(m.data);
    } catch { toast.error('Failed to load sustainability data'); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await sustainabilityAPI.updateMetrics(form);
      toast.success('Metrics updated');
      setEditing(false);
      loadAll();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const esgScore = report?.esg_score || 0;
  const esgColor = esgScore >= 70 ? '#1F7A5A' : esgScore >= 40 ? '#B89B5E' : '#B3261E';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Leaf className="w-6 h-6 text-[#1F7A5A]" /> Sustainability & ESG</h1>
          <p className="text-sm text-[#5B616E] mt-1">Environmental, social, and governance tracking for your newsroom</p>
        </div>
        <Button onClick={() => setEditing(!editing)} variant={editing ? 'default' : 'outline'} className={editing ? 'bg-[#121316] text-white' : ''}>
          {editing ? 'Cancel Edit' : 'Edit Metrics'}
        </Button>
      </div>

      {/* ESG Score */}
      {report && (
        <Card className="bg-white border-[#E7E0D6]">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5B616E]">Overall ESG Score</p>
                <p className="text-5xl font-bold mt-1" style={{ color: esgColor }}>{esgScore}/100</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">Impact Summary</p>
                <p className="text-xs text-[#5B616E] mt-1">{report.impact_summary?.articles_published?.toLocaleString()} articles published</p>
                <p className="text-xs text-[#5B616E]">{report.impact_summary?.digital_readers?.toLocaleString()} digital readers</p>
                <p className="text-xs text-green-600 font-medium">{report.impact_summary?.estimated_trees_saved} trees saved (est.)</p>
              </div>
            </div>
            <div className="h-3 bg-[#E7E0D6] rounded-full mt-4">
              <div className="h-3 rounded-full transition-all" style={{ width: `${esgScore}%`, background: esgColor }} />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: Zap, label: 'Renewable Energy', value: `${metrics?.renewable_energy_pct || 0}%`, target: '100%', color: '#B89B5E' },
          { icon: Leaf, label: 'CO₂ Offset', value: `${metrics?.carbon_offset_kg || 0} kg`, target: '', color: '#1F7A5A' },
          { icon: FileText, label: 'Paper Saved', value: `${metrics?.paper_saved_kg || 0} kg`, target: '', color: '#1E5A8A' },
          { icon: Globe, label: 'Green Hosting', value: metrics?.green_hosting ? 'Yes' : 'No', target: 'Target: Yes', color: metrics?.green_hosting ? '#1F7A5A' : '#B3261E' },
        ].map(item => (
          <Card key={item.label} className="bg-white border-[#E7E0D6]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <item.icon className="w-5 h-5" style={{ color: item.color }} />
                <span className="text-xs text-[#5B616E]">{item.label}</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: item.color }}>{item.value}</p>
              {item.target && <p className="text-[10px] text-[#5B616E]">{item.target}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Goals from report */}
      {report?.goals && (
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm">Climate Commitments</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(report.goals).map(([key, val]) => (
              <div key={key} className="flex justify-between items-center text-sm py-1.5 border-b border-[#E7E0D6] last:border-0">
                <span className="text-[#5B616E] capitalize">{key.replace(/_/g, ' ')}</span>
                <Badge variant="outline" className="text-xs">{String(val)}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Edit form */}
      {editing && (
        <Card className="bg-white border-[#E7E0D6]">
          <CardHeader><CardTitle className="text-sm">Update Metrics</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium mb-1 block">Renewable Energy (%)</label><Input type="number" min={0} max={100} value={form.renewable_energy_pct || 0} onChange={e => setForm(f => ({ ...f, renewable_energy_pct: parseFloat(e.target.value) || 0 }))} /></div>
              <div><label className="text-xs font-medium mb-1 block">CO₂ Offset (kg)</label><Input type="number" value={form.carbon_offset_kg || 0} onChange={e => setForm(f => ({ ...f, carbon_offset_kg: parseFloat(e.target.value) || 0 }))} /></div>
              <div><label className="text-xs font-medium mb-1 block">Paper Saved (kg)</label><Input type="number" value={form.paper_saved_kg || 0} onChange={e => setForm(f => ({ ...f, paper_saved_kg: parseFloat(e.target.value) || 0 }))} /></div>
              <div><label className="text-xs font-medium mb-1 block">Diversity Score (/10)</label><Input type="number" min={0} max={10} step={0.1} value={form.diversity_score || ''} onChange={e => setForm(f => ({ ...f, diversity_score: parseFloat(e.target.value) || null }))} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!form.green_hosting} onChange={e => setForm(f => ({ ...f, green_hosting: e.target.checked }))} className="rounded" />
              Green-certified hosting
            </label>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1F7A5A] text-white hover:bg-[#1A6B4E]">{saving ? 'Saving…' : 'Save Metrics'}</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
